import { resolveThemeColor } from '../../../constants/theme-aware-colors';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { useListLinkNavigation } from '../../hooks/useListLinkNavigation';
import type {
	FieldConfiguration,
	ListBuilderCallbacks,
	TableRow,
} from '../../types';
import { getFieldDisplayProperties } from './displayHelpers';

interface RelativeDateDisplayProps {
	field: FieldConfiguration;
	value: any;
	data: TableRow;
	listKey?: string;
	objects?: Array<{ objectTable: string; baseUrl?: string }>;
	callbacks?: ListBuilderCallbacks;
}

function getRelativeDate(
	sourceDate: string | Date,
	referenceDate: Date,
): string {
	if (!sourceDate) {
		return '';
	}

	try {
		const source =
			typeof sourceDate === 'string' ? new Date(sourceDate) : sourceDate;

		if (isNaN(source.getTime())) {
			return '';
		}

		// Calculate difference in milliseconds
		const diffMs = source.getTime() - referenceDate.getTime();
		const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

		// Same day
		if (diffDays === 0) {
			return 'today';
		}

		// Create RelativeTimeFormat instance
		const rtf = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

		const absDiffDays = Math.abs(diffDays);

		// Select appropriate unit based on the difference
		if (absDiffDays === 1) {
			return rtf.format(diffDays, 'day');
		} else if (absDiffDays < 7) {
			return rtf.format(diffDays, 'day');
		} else if (absDiffDays < 28) {
			const weeks = Math.round(diffDays / 7);
			return rtf.format(weeks, 'week');
		} else if (absDiffDays < 365) {
			const months = Math.round(diffDays / 30);
			return rtf.format(months, 'month');
		} else {
			const years = Math.round(diffDays / 365);
			return rtf.format(years, 'year');
		}
	} catch (error) {
		// Error: Error calculating relative date:
		return '';
	}
}

export function RelativeDateDisplay({
	field,
	value,
	data,
	listKey,
	objects,
	callbacks,
}: RelativeDateDisplayProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();
	const { styleConfig, link, dataConfig } = field;

	// Use the shared link navigation hook
	const { linkUrl, handleLinkClick, isCallback } = useListLinkNavigation({
		field,
		data,
		listKey,
		objects,
		callbacks,
	});

	// Note: For relative date display, we don't apply value mapping to the date itself
	// but we still evaluate color based on the original value
	const { colorValue } = getFieldDisplayProperties(field, value, data);

	if (!value && !dataConfig?.defaultValue) {
		return null;
	}

	// Determine reference date
	let referenceDate = new Date();
	const formatOptions = styleConfig.formatOptions;

	if (formatOptions?.relativeBase === 'field' && formatOptions.relativeField) {
		const refFieldName = formatOptions.relativeField.fieldName;
		const refValue = data[refFieldName];
		if (refValue) {
			const refDate = new Date(refValue);
			if (!isNaN(refDate.getTime())) {
				referenceDate = refDate;
			}
		}
	}

	const relativeText = value
		? getRelativeDate(value, referenceDate)
		: dataConfig?.defaultValue || '';

	// Determine text color based on relative position
	const resolvedTextColor = resolveThemeColor(colorValue?.text, isDark);
	let textColor = resolvedTextColor ?? colors.values.textPrimary;

	// If using color rules for overdue/upcoming status
	if (styleConfig.colorRule.type === 'static' && resolvedTextColor == null) {
		const source = new Date(value);
		const diffMs = source.getTime() - referenceDate.getTime();
		const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

		if (diffDays < 0) {
			// Past/Overdue - red
			textColor = colors.values.statusError;
		} else if (diffDays === 0) {
			// Today - amber
			textColor = colors.values.statusWarning;
		} else if (diffDays <= 7) {
			// Near future - blue
			textColor = colors.values.statusInfo;
		}
	}

	// Check if this field has linking enabled
	const isLinkEnabled = link?.enabled && (data?.id || data?._id);

	if (isLinkEnabled) {
		if (!isCallback && linkUrl != null) {
			return (
				<a
					href={linkUrl}
					onClick={handleLinkClick}
					className="hover:underline transition-all no-underline"
					style={{ color: colors.values.primary }}
				>
					{t(relativeText)}
				</a>
			);
		}

		return (
			<button
				onClick={handleLinkClick}
				className="hover:underline transition-all text-left"
				style={{ color: colors.values.primary }}
				type="button"
			>
				{t(relativeText)}
			</button>
		);
	}

	return <span style={{ color: textColor }}>{t(relativeText)}</span>;
}
